import { addDays, format, getDate } from 'date-fns';
import iconv from 'iconv-lite';
import { ClassifierCode } from 'api';
import { TransformedSelect } from 'components/pages/Lz/LzTab/common/interface/interface';
import { IOptions } from '../types/common.types';

export const stringForMask = (pan: string) => {
  const n = pan.length;
  const f = pan.substring(0, 6) + '*'.repeat(n - 6) + pan.substring(n - 4, n);
  return f.match(/.{1,4}/g)?.join(' ');
};

export const spaceCard = (pan: string) => pan.match(/.{1,4}/g)?.join(' ');

export const cardString = (pan: string) =>
  pan
    .replace(/\s/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();

export const pxToRem = (px: number, unit: boolean = false) => (unit ? `${px / 16}rem` : px / 16);

export const transformToSelect = ({ id, name }: { id: string; name: string }) => ({
  value: id,
  label: name,
});

export const shortTransformToSelect = <T extends { id: number; name: string }>(data: T[]): IOptions[] =>
  data.map(item => transformToSelect({ id: String(item.id), name: item.name }));

export const shortTransformToSelectPrice = <T extends { id: number; name: string; classifier?: string }>(
  data: T[],
): TransformedSelect[] =>
  data.map(item =>
    transformToSelect({
      id: String(item.id),
      name: `${item.name}, ${item.classifier || ''}`,
    }),
  );

export const shortTransformToSelectWithBrand = <T extends { id: number; pan: string }>(data: T[]): IOptions[] =>
  data.map(item => transformToSelect({ id: String(item.id), name: item.pan }));

export const shortTransformToSelectExtended = <T extends { id: number; name: string; lzName: string }>(
  data: T[],
): IOptions[] => data.map(item => transformToSelect({ id: String(item.id), name: `${item.lzName} - ${item.name}` }));

export const replaceNull = <T>(data: T): T => {
  const result = { ...data };
  // Убираем null на пустую строку, чтобы форма отрабатывала ожидаемо
  for (const resultKey in result) {
    if (result[resultKey] === null) {
      // @ts-ignore
      result[resultKey] = '';
    }
  }

  return result;
};

type TType = 'round';
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/floor
// @ts-ignore
function decimalAdjust(type: TType, value, exp) {
  // If the exp is undefined or zero...
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math[type](value);
  }
  value = Number(`${value}`.replace(/\s/g, ''));
  exp = +exp;
  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }
  // Shift
  value = value.toString().split('e');
  // eslint-disable-next-line
  value = Math[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
  // Shift back
  // eslint-disable-next-line
  value = value.toString().split('e');
  // eslint-disable-next-line
  return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
}
// @ts-ignore
export const round10 = (value, exp) => decimalAdjust('round', value, exp);

export const isTextContentOverflowing = (element: HTMLElement): boolean =>
  (element.scrollWidth || 0) > (element.offsetWidth || 0);

export const downloadFromURL = (downloadURL: string, fileName?: string) => {
  fetch(downloadURL).then(response => {
    response.blob().then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'file';
      a.click();
    });
  });
};

export const downloadFromFile = (content: string, fileName?: string) => {
  const el = document.createElement('a');
  el.href = `data:text/csv;charset=utf-8,\uFEFF${encodeURIComponent(content)}`;
  el.download = fileName ?? 'file';

  el.click();
};

export const downloadFileWin1251 = (content: string, fileName: string) => {
  const encoded = iconv.encode(content, 'win1251');
  const file = new Blob([encoded], { type: 'text/plain;charset=windows-1251' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(file);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
};

export const getTotalPaymentWithCommission = (clientPaymentAmount: number, commission?: number): number | undefined =>
  commission && (clientPaymentAmount * commission) / 100;

export const userStatusTranslftrate = (status: string) => {
  switch (status) {
    case 'passportSn':
      return 'Номер и серия паспорта';
    case 'name':
      return 'Имя';
    case 'surname':
      return 'Фамилия';
    case 'patronymic':
      return 'Отчество';
    case 'dateOfBirth':
      return 'Дата Рождения';
    case 'passportIssuedDate':
      return 'Дата выдачи паспорта';
    case 'passportIssuedBy':
      return 'Кем выдан паспорт';
    case 'placeOfBirth':
      return 'Место рождения';
  }
};

export const scanTypeMap: Record<string, string> = {
  'PSA_SCAN': 'акта',
  'PASSPORT_SCAN': 'паспорта',
};

export const statusClassifierCodeTranslate = (status: ClassifierCode) => {
  switch (status) {
    case ClassifierCode.Color:
      return 'Цветные металлы';
    case ClassifierCode.Black:
      return 'Черные металлы';
    case ClassifierCode.WASTE_PAPER:
      return 'Макулатура';
    default:
      return '';
  }
};

export const convertFromDatetoMonth = (monthData: string) => {
  const month = monthData.split('-')[1];
  switch (month) {
    case '01':
      return 'Январь';
    case '02':
      return 'Февраль';
    case '03':
      return 'Март';
    case '04':
      return 'Апрель';
    case '05':
      return 'Май';
    case '06':
      return 'Июнь';
    case '07':
      return 'Июль';
    case '08':
      return 'Август';
    case '09':
      return 'Сентябрь';
    case '10':
      return 'Октябрь';
    case '11':
      return 'Ноябрь';
    case '12':
      return 'Декабрь';
  }
};

export enum DataColors {
  BLACK_METAL = '#000',
  COLOR_METAL = '#fea617',
  WASTE_PAPER = '#808080',
}

export const mapDataShortValues = (date: string) => {
  const monthsNumber = date.split('-')[1];
  const year = date.split('-')[0];

  switch (monthsNumber) {
    case '01':
      return `Янв. ${year}`;
    case '02':
      return `Фев. ${year}`;
    case '03':
      return `Март ${year}`;
    case '04':
      return `Апр. ${year}`;
    case '05':
      return `Май ${year}`;
    case '06':
      return `Июн. ${year}`;
    case '07':
      return `Июл. ${year}`;
    case '08':
      return `Авг. ${year}`;
    case '09':
      return `Сент. ${year}`;
    case '10':
      return `Окт. ${year}`;
    case '11':
      return `Ноя. ${year}`;
    case '12':
      return `Дек. ${year}`;
  }
};

export const weekLabel = (date: string) => {
  const monthsNumber = date.split('-')[1];

  const label = plusDaysToData(date);

  switch (monthsNumber) {
    case '01':
      return label;
    case '02':
      return label;
    case '03':
      return label;
    case '04':
      return label;
    case '05':
      return label;
    case '06':
      return label;
    case '07':
      return label;
    case '08':
      return label;
    case '09':
      return label;
    case '10':
      return label;
    case '11':
      return label;
    case '12':
      return label;
  }
};

export const yearLabel = (date: string) => {
  const monthsNumber = date.split('-')[1];

  switch (monthsNumber) {
    case '01':
      return 'Январь';
    case '02':
      return 'Февраль';
    case '03':
      return 'Март';
    case '04':
      return 'Апрель';
    case '05':
      return 'Май';
    case '06':
      return 'Июнь';
    case '07':
      return 'Июль';
    case '08':
      return 'Август';
    case '09':
      return 'Сентябрь';
    case '10':
      return 'Октябрь';
    case '11':
      return 'Ноябрь';
    case '12':
      return 'Декабрь';
  }
};

export const dayLabel = (date: string) => {
  const result = getDate(new Date(date));

  const month = format(new Date(date), 'MM');

  const dayOfWeek = format(new Date(date), 'EEEE');
  const dayOfWeekTranslated = daysOfWeekTranslate(dayOfWeek);

  switch (result) {
    case 1:
      return String(`${dayOfWeekTranslated} 01.${month}`);
    case 2:
      return `${dayOfWeekTranslated} 02.${month}`;
    case 3:
      return `${dayOfWeekTranslated} 03.${month}`;
    case 4:
      return `${dayOfWeekTranslated} 04.${month}`;
    case 5:
      return `${dayOfWeekTranslated} 05.${month}`;
    case 6:
      return `${dayOfWeekTranslated} 06.${month}`;
    case 7:
      return `${dayOfWeekTranslated} 07.${month}`;
    case 8:
      return `${dayOfWeekTranslated} 08.${month}`;
    case 9:
      return `${dayOfWeekTranslated} 09.${month}`;
    case 10:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 11:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 12:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 13:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 14:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 15:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 16:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 17:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 18:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 19:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 20:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 21:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 22:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 23:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 24:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 25:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 26:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 27:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 28:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 29:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 30:
      return `${dayOfWeekTranslated} ${result}.${month}`;
    case 31:
      return `${dayOfWeekTranslated} ${result}.${month}`;
  }
};

export const hourLabel = (date: string, filterDay: string | undefined) => {
  const hour = new Date(date).getHours();

  const monthWithZero = date.split('-')[1];
  const day = filterDay && format(new Date(filterDay), 'dd');

  switch (hour) {
    case 0:
      return `00:00 ${day}.${monthWithZero}`;
    case 1:
      return `01:00 ${day}.${monthWithZero}`;
    case 2:
      return `02:00 ${day}.${monthWithZero}`;
    case 3:
      return `03:00 ${day}.${monthWithZero}`;
    case 4:
      return `04:00 ${day}.${monthWithZero}`;
    case 5:
      return `05:00 ${day}.${monthWithZero}`;
    case 6:
      return `06:00 ${day}.${monthWithZero}`;
    case 7:
      return `07:00 ${day}.${monthWithZero}`;
    case 8:
      return `08:00 ${day}.${monthWithZero}`;
    case 9:
      return `09:00 ${day}.${monthWithZero}`;
    case 10:
      return `10:00 ${day}.${monthWithZero}`;
    case 11:
      return `11:00 ${day}.${monthWithZero}`;
    case 12:
      return `12:00 ${day}.${monthWithZero}`;
    case 13:
      return `13:00 ${day}.${monthWithZero}`;
    case 14:
      return `14:00 ${day}.${monthWithZero}`;
    case 15:
      return `15:00 ${day}.${monthWithZero}`;
    case 16:
      return `16:00 ${day}.${monthWithZero}`;
    case 17:
      return `17:00 ${day}.${monthWithZero}`;
    case 18:
      return `18:00 ${day}.${monthWithZero}`;
    case 19:
      return `19:00 ${day}.${monthWithZero}`;
    case 20:
      return `20:00 ${day}.${monthWithZero}`;
    case 21:
      return `21:00 ${day}.${monthWithZero}`;
    case 22:
      return `22:00 ${day}.${monthWithZero}`;
    case 23:
      return `23:00 ${day}.${monthWithZero}`;
  }
};

const daysOfWeekTranslate = (day: string) => {
  switch (day) {
    case 'Monday':
      return 'Понедельник\n';
    case 'Tuesday':
      return 'Вторник\n';
    case 'Wednesday':
      return 'Среда\n';
    case 'Thursday':
      return 'Четверг\n';
    case 'Friday':
      return 'Пятница\n';
    case 'Saturday':
      return 'Суббота\n';
    case 'Sunday':
      return 'Воскресенье\n';
  }
};

const plusDaysToData = (currentDay: string) => {
  const fromPlusDay = addDays(new Date(currentDay), 7);

  const month = format(new Date(fromPlusDay), 'MM');
  const day = format(new Date(fromPlusDay), 'dd');

  const currentMonth = format(new Date(currentDay), 'MM');
  const currentD = format(new Date(currentDay), 'dd');

  return `${currentD}.${currentMonth} - ${day}.${month}`;
};

export const periodTransle = (period: string) => {
  switch (period) {
    case 'HOUR':
      return 'Час';
    case 'DAY':
      return 'День';
    case 'WEEK':
      return 'Неделя';
    case 'MONTH':
      return 'Месяц';
  }
};

export enum DocumentsTypes {
  LICENSE = 'LICENSE',
  EQUIPMENT = 'EQUIPMENT',
  RADIATION_CONTROL = 'RADIATION_CONTROL',
  RADIATION_DETECTION = 'RADIATION_DETECTION',
  EXPLOSIVES_DETECTION = 'EXPLOSIVES_DETECTION',
  METAL_RADIATION_EXPLOSIVES_INSPECTOR = 'METAL_RADIATION_EXPLOSIVES_INSPECTOR',
  OWNERSHIP_LEASE = 'OWNERSHIP_LEASE',
}

export const checkCardNumberIsSame = (LzClientBusinessCardOptions: IOptions[], lzClientPaymentCardNumber: string) => {
  for (let i = 0; i < LzClientBusinessCardOptions?.length; ++i) {
    if (
      Number(LzClientBusinessCardOptions[i]?.label.replace(/\s/g, '').substring(0, 6)) ===
        Number(lzClientPaymentCardNumber.replace(/\s/g, '').substring(0, 6)) &&
      Number(
        LzClientBusinessCardOptions[i]?.label
          .replace(/\s/g, '')
          .substring(LzClientBusinessCardOptions[i]?.label.replace(/\s/g, '').length - 4),
      ) ===
        Number(
          lzClientPaymentCardNumber
            ?.replace(/\s/g, '')
            .substring(lzClientPaymentCardNumber?.replace(/\s/g, '').length - 4),
        )
    ) {
      return true;
    }
  }
  return false;
};

export const checkCardNumbersCount = (LzClientBusinessCardOptions: IOptions[], lzClientPaymentCardNumber: string) => {
  for (let i = 0; i < LzClientBusinessCardOptions?.length; ++i) {
    if (
      LzClientBusinessCardOptions[i]?.label.replace(/\s/g, '')?.length ===
      lzClientPaymentCardNumber.replace(/\s/g, '')?.length
    ) {
      return true;
    }
  }
  return false;
};

export const errorMessageBusinessCard = {
  5100: 'Отсутствует связка логин/пароль от платежного шлюза. Обратитесь в поддержку.',
  2101: 'Связка логин/пароль от платежного шлюза некорректна. Обратитесь в поддержку.',
};

export const stringToBoolean = (value: string): boolean => value === 'true';

export enum ETimer {
  PAUSED = 'PAUSED',
  TICKING = 'TICKING',
}
